// biome-ignore lint/style/useImportType: React is used as a module by MUI, so changing it to `import type` would be incorrect
import React from "react";
import type { FormikConfig, FormikProps } from "formik";
import { Form, Formik } from "formik";

type FormikWrapperProps<Values> = {
	children: ((formikProps: FormikProps<Values>) => React.ReactNode) | React.ReactNode;
	validateOnBlur?: boolean;
} & Omit<FormikConfig<Values>, "children">;

/**
 * Custom wrapper for Formik.
 *
 * This wrapper sets the following defaults:
 *
 * `validateOnBlur: false`: Prevents Formik from validating when a field is focused and unfocused as long as no changes have been made in the field.
 *
 * All config values and props from Formik are still callable and accessible like the standard Formik component.
 *
 * @example Basic usage with direct JSX children
 *
 * ```
 * <FormikWrapper
 *   initialValues={initialValues}
 *   validationSchema={validationSchema}
 *   onSubmit={onSubmit}
 * >
 *   <Input name="name" label="Name" />
 * </FormikWrapper>
 * ```
 *
 * @example Advanced usage using the props from Formik
 *
 * ```
 * <FormikWrapper
 *   initialValues={initialValues}
 *   validationSchema={validationSchema}
 *   onSubmit={onSubmit}
 * >
 *   {formikProps => (
 *     <>
 *       <Input name="name" label="Name" />
 *       <CustomComponent
 *         values={formikProps.values}
 *         setFieldValue={formikProps.setFieldValue}
 *         errors={formikProps.errors}
 *       />
 *     </>
 *   )}
 * </FormikWrapper>
 * ```
 */

export function FormikWrapper<Values>({
	children,
	validateOnBlur = false,
	...props
}: FormikWrapperProps<Values>) {
	const renderForm = (formikProps: FormikProps<Values>) => {
		// Return the children as a function when the formikProps are called ({( formikProps ) => ...})
		// Otherwise return the children as a JSX element
		const renderedChildren = typeof children === "function" ? children(formikProps) : children;

		return <Form>{renderedChildren}</Form>;
	};

	return (
		<Formik validateOnBlur={validateOnBlur} {...props}>
			{renderForm}
		</Formik>
	);
}
